import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react-lite";

import classes from "./OAuthModal.module.scss";
import Modal from "../../../../organisms/Modal";
import Icon from "../../../../atoms/Icon";
import Button from "../../../../atoms/Button";
import { NAMES as ICON_NAMES } from "../../../../atoms/Icon";
import Logotype, { TYPES as LOGOTYPE_TYPES } from "../../../../atoms/Logotype";
import Loader from "../../../../atoms/Loader";
import { StoreContext } from "../../../../../contexts";
import Cookies from "../../../../../utilities/cookies";
import { COOKIES } from "../../../../../constants";
import { WRITING_COMPETITON_DATA_KEY } from "../../../WritingCompetition/WritingCompetition";

const OAuthModal = observer(({
    opened,
    onAllow,
    onDeny,
    oauthParams,
    user
}) => {
    const { authorization, common } = useContext(StoreContext);

    useEffect(() => {
        if (opened && oauthParams?.client_id) {
            authorization.getOauthClientDetails(oauthParams.client_id);
        }
    }, [opened, oauthParams?.client_id]);

    if (!user) return null;

    const clientDetails = authorization.oauthClientDetails;
    const appDomain = oauthParams?.redirect_uri ? new URL(oauthParams.redirect_uri).hostname : "example-site.com";
    const clientName = clientDetails?.name || appDomain;
    const clientWebsite = clientDetails?.website_url || appDomain;
    const privacyUrl = clientDetails?.privacy_url || "#";
    const termsUrl = clientDetails?.terms_url || "#";

    const onLogout = () => {
        Cookies.delete({ name: COOKIES.AUTO_SIGNIN });
        Cookies.delete({ name: COOKIES.CART_ID });
        common.set(WRITING_COMPETITON_DATA_KEY, null);
        Cookies.delete({ name: COOKIES.GOOGLE_PROVIDER });
        authorization.signOut();
        onDeny();
    };

    return (
        <Modal opened={opened} onClose={onDeny} flexible>
            <div className={classes.container}>
                <div className={classes.header}>
                    <Logotype type={LOGOTYPE_TYPES.DEFAULT} size="s" />
                </div>

                <h2 className={classes.title}>
                    <a href={clientWebsite} target="_blank" rel="noopener noreferrer" className={classes.clientName}>{clientName}</a> wants to access your Maor account
                </h2>
                <p className={classes.subtitle}>
                    Review what this app will be able to see before continuing.
                </p>

                <div className={classes.userProfile}>
                    <div className={classes.userInitials}>
                        {user.first_name?.charAt(0).toUpperCase()}
                        {user.last_name?.charAt(0).toUpperCase()}
                    </div>
                    <div className={classes.userInfo}>
                        <div className={classes.userName}>{user.first_name} {user.last_name}</div>
                        <div className={classes.userEmail}>{user.email}</div>
                    </div>
                    <div className={classes.switchAccount} onClick={onLogout}>
                        Switch account
                    </div>
                </div>

                <div className={classes.permissionsSection}>
                    <h3 className={classes.sectionTitle}>THIS APP WILL BE ABLE TO SEE</h3>

                    {/* <div className={classes.permissionItem}>
                        <div className={classes.iconWrapper}>
                            <Icon name={ICON_NAMES.CREDIT_CARD} size="xs" />
                        </div>
                        <div className={classes.permissionContent}>
                            <div className={classes.permissionLabel}>Your account ID</div>
                            <div className={classes.permissionDesc}>A unique identifier for your Maor account</div>
                        </div>
                    </div> */}

                    <div className={classes.permissionItem}>
                        <div className={classes.iconWrapper}>
                            <Icon name={ICON_NAMES.LETTER} size="xs" />
                        </div>
                        <div className={classes.permissionContent}>
                            <div className={classes.permissionLabel}>Email address & full name</div>
                            <div className={classes.permissionDesc}>Your registered email and display name</div>
                        </div>
                    </div>

                    <div className={classes.permissionItem}>
                        <div className={classes.iconWrapper}>
                            <Icon name={ICON_NAMES.USERS} size="xs" />
                        </div>
                        <div className={classes.permissionContent}>
                            <div className={classes.permissionLabel}>Membership plan</div>
                            <div className={classes.permissionDesc}>Your current plan tier</div>
                        </div>
                    </div>

                    <div className={classes.permissionItem}>
                        <div className={classes.iconWrapper}>
                            <Icon name={ICON_NAMES.CHECK_MARK} size="xs" />
                        </div>
                        <div className={classes.permissionContent}>
                            <div className={classes.permissionLabel}>Subscription status</div>
                            <div className={classes.permissionDesc}>Whether your subscription is active</div>
                        </div>
                    </div>
                </div>

                <p className={classes.footerDisclaimer}>
                    By allowing access, you agree to {clientName}&apos;s <a href={termsUrl} target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href={privacyUrl} target="_blank" rel="noopener noreferrer">Privacy Policy</a>. Maor.org will not share your password or payment details.
                </p>

                <div className={classes.actions}>
                    <Button onClick={onLogout} disabled={authorization.isLoadingAuthenticatePassword} >
                        Deny
                    </Button>
                    <Button onClick={onAllow} disabled={authorization.isLoadingAuthenticatePassword} >
                        {authorization.isLoadingAuthenticatePassword ? <Loader size="medium" /> : "Allow access"}
                    </Button>
                </div>
            </div>
        </Modal>
    );
});

OAuthModal.propTypes = {
    opened: PropTypes.bool.isRequired,
    onAllow: PropTypes.func.isRequired,
    onDeny: PropTypes.func.isRequired,
    oauthParams: PropTypes.object,
    user: PropTypes.object
};

export default OAuthModal;
